import { useCallback, useEffect, useMemo, useState } from 'react';
import type { DaySnapshot, RunLog } from 'walkout-engine';
import { ALL_PARAMETERS, readParameter } from 'walkout-engine';
import { useConfig } from './state/useConfig.ts';
import { usePlayback, useRun } from './state/useSimulation.ts';
import { scenarioById } from './state/scenarios.ts';
import { isCurrentEngine, readLocation, shareUrl, writeLocation } from './state/urlState.ts';
import { SetupPanel } from './components/SetupPanel.tsx';
import { TopStrip } from './components/TopStrip.tsx';
import { Stage } from './components/Stage.tsx';
import { Feed } from './components/Feed.tsx';
import { PlaybackControls } from './components/PlaybackControls.tsx';
import { Outcome } from './components/Outcome.tsx';
import { SharePanel } from './components/SharePanel.tsx';
import { Compare } from './components/Compare.tsx';
import './App.css';

/**
 * A readable list of what differs between two configurations.
 *
 * Built from the parameter registry rather than a hand-maintained list, so a parameter
 * added to the model shows up here without anyone remembering to wire it in.
 */
function describeChanges(baseline: RunLog, variant: RunLog): string[] {
  const changes: string[] = [];
  const format = (value: number, unit: string): string =>
    unit === 'share' ? `${(value * 100).toFixed(0)}%` : unit === 'days' ? `${Math.round(value)}d` : value.toFixed(2);

  for (const scope of ['movement', 'media', 'regime'] as const) {
    for (const meta of ALL_PARAMETERS.filter((p) => p.scope === scope)) {
      const a = readParameter(baseline.config[scope], meta.path);
      const b = readParameter(variant.config[scope], meta.path);
      if (a === undefined || b === undefined || Math.abs(a - b) < meta.step / 1000) continue;
      changes.push(`${meta.label}: ${format(a, meta.unit)} → ${format(b, meta.unit)}`);
    }
  }

  for (const sector of variant.config.sectors) {
    const before = baseline.config.sectors.find((s) => s.id === sector.id);
    if (!before) {
      changes.push(`Added sector: ${sector.name}`);
      continue;
    }
    for (const meta of ALL_PARAMETERS.filter((p) => p.scope === 'sector')) {
      const a = readParameter(before, meta.path);
      const b = readParameter(sector, meta.path);
      if (a === undefined || b === undefined || Math.abs(a - b) < meta.step / 1000) continue;
      changes.push(`${sector.name} — ${meta.label}: ${format(a, meta.unit)} → ${format(b, meta.unit)}`);
    }
  }

  for (const pillar of variant.config.pillars) {
    const before = baseline.config.pillars.find((p) => p.id === pillar.id);
    if (!before) continue;
    for (const meta of ALL_PARAMETERS.filter((p) => p.scope === 'pillar')) {
      const a = readParameter(before, meta.path);
      const b = readParameter(pillar, meta.path);
      if (a === undefined || b === undefined || Math.abs(a - b) < meta.step / 1000) continue;
      changes.push(`${pillar.name} — ${meta.label}: ${format(a, meta.unit)} → ${format(b, meta.unit)}`);
    }
  }

  if (baseline.config.seed !== variant.config.seed) {
    changes.push(`Seed: ${baseline.config.seed} → ${variant.config.seed}`);
  }
  return changes.slice(0, 12);
}

export function App() {
  const config = useConfig();
  const state = useRun(config.config);
  const [baseline, setBaseline] = useState<RunLog | null>(null);
  const [staleLink, setStaleLink] = useState(false);

  const run = state.status === 'ready' ? state.run : null;
  const lastDay = run ? run.days.length - 1 : 0;
  const playback = usePlayback(lastDay);

  // Restore a shared run once, on first load.
  useEffect(() => {
    const shared = readLocation();
    if (!shared) return;
    const scenario = scenarioById(shared.scenarioId);
    if (!scenario) return;
    if (!isCurrentEngine(shared)) setStaleLink(true);
    config.selectScenario(scenario);
    config.replaceOverrides(shared.overrides);
    // Deliberately once: re-running this on every render would fight the user's edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the address bar in step, so the link is always shareable without a "share" step.
  useEffect(() => {
    writeLocation(config.scenario.id, config.overrides);
  }, [config.scenario.id, config.overrides]);

  const day: DaySnapshot | null = useMemo(() => {
    if (!run) return null;
    return run.days[Math.min(playback.day, run.days.length - 1)] ?? null;
  }, [run, playback.day]);

  const url = useMemo(
    () => shareUrl(config.scenario.id, config.overrides),
    [config.scenario.id, config.overrides],
  );

  const changes = useMemo(
    () => (baseline && run ? describeChanges(baseline, run) : []),
    [baseline, run],
  );

  const pinBaseline = useCallback(() => {
    if (run) setBaseline(run);
  }, [run]);

  const atEnd = playback.day >= lastDay;

  return (
    <div className="app">
      <SetupPanel state={config} />

      <main className="app__main">
        {staleLink && (
          <div className="app__banner">
            This link was made with an older version of the model, so it may not replay
            exactly as the sender saw it.
          </div>
        )}

        {state.status === 'failed' && (
          <div className="app__message app__message--error">
            <h2>That configuration cannot run</h2>
            <p>{state.error}</p>
          </div>
        )}

        {!run && state.status !== 'failed' && (
          <div className="app__message">
            <h2>Simulating</h2>
          </div>
        )}

        {run && day && (
          <>
            <TopStrip run={run} day={day} />
            <div className="app__body">
              <Stage run={run} day={day} />
              <Feed run={run} day={playback.day} />
            </div>

            {atEnd && (
              <div className="app__end">
                <Outcome run={run} />
                {baseline ? (
                  <Compare
                    baseline={baseline}
                    variant={run}
                    changes={changes}
                    onClose={() => setBaseline(null)}
                  />
                ) : (
                  <div className="app__compare-prompt">
                    <p>
                      Pin this run, then change one thing. With the seed fixed, whatever
                      moves is caused by the change rather than by luck.
                    </p>
                    <button type="button" onClick={pinBaseline}>
                      pin as baseline
                    </button>
                  </div>
                )}
                <SharePanel run={run} url={url} />
              </div>
            )}

            <PlaybackControls run={run} playback={playback} />
          </>
        )}
      </main>
    </div>
  );
}
